import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, FileText, FolderKanban, Headphones, PackageCheck, Receipt } from "lucide-react";
import { rpc, useAction, useOrg } from "@vixio/data";
import {
  Button, EmptyState, KpiRow, KpiTile, Panel, Skeleton, StatusPill, Timeline, formatDate, formatDateTime, formatInt, formatMoney,
  formatMoneyShort, type TimelineItem,
} from "@vixio/ui";
import {
  ITEM_STATUS, KIND_LABEL, useItems, useLibrary, usePortalMe, useProjects, useTimeline, type ItemKind, type PortalItem, type TimelineEntry,
} from "./api";
import { Block, LinkButton, NextStep, PdfButton, Progress, daysUntil, type StepTone } from "./ui";

interface Step {
  key: string;
  tone: StepTone;
  icon: ReactNode;
  title: ReactNode;
  desc?: ReactNode;
  action?: ReactNode;
  rank: number;
}

function invoiceStep(i: PortalItem): Step {
  const d = daysUntil(i.due_date);
  const overdue = d !== null && d < 0;
  const soon = d !== null && d >= 0 && d <= 10;
  return {
    key: i.id,
    tone: overdue ? "crit" : soon ? "warn" : "accent",
    icon: <Receipt />,
    title: overdue ? `Fatura vencida: ${formatMoney(i.amount_cents)}` : `Fatura de ${formatMoney(i.amount_cents)}`,
    desc: `${i.title}${i.due_date ? ` · vence em ${formatDate(i.due_date)}` : ""}`,
    action: <LinkButton to="/portal/financeiro" small primary={overdue}>Ver como pagar</LinkButton>,
    rank: overdue ? 0 : soon ? 2 : 5,
  };
}

/** Início do portal: próximos passos, números e projetos. As reuniões vêm do módulo agenda. */
export function PortalHomePanel() {
  const org = useOrg();
  const me = usePortalMe(org.id).data;
  const itemsQ = useItems(org.id, ["deliverable", "receivable", "ticket"]);
  const projectsQ = useProjects(org.id);
  if (!me || itemsQ.isLoading) return <Skeleton lines={4} />;
  const items = itemsQ.data ?? [];
  const projects = projectsQ.data ?? [];
  const toApprove = items.filter((i) => i.kind === "deliverable" && i.needs_action);
  const openInvoices = items.filter((i) => i.kind === "receivable" && (i.status === "open" || i.status === "partial"));
  const waitingTickets = items.filter((i) => i.kind === "ticket" && i.status === "waiting_client");
  const openTickets = items.filter((i) => i.kind === "ticket" && ["open", "in_progress", "waiting_client"].includes(i.status ?? ""));
  const activeProjects = projects.filter((p) => p.status === "active" || p.status === "planning" || p.status === "paused");

  const steps: Step[] = [
    ...toApprove.map<Step>((i) => ({
      key: i.id,
      tone: "warn",
      icon: <PackageCheck />,
      title: `Aprovar a entrega: ${i.title}`,
      desc: [i.subtitle, i.due_date ? `prazo ${formatDate(i.due_date)}` : null].filter(Boolean).join(" · "),
      action: me.can_approve ? <LinkButton to="/portal/entregaveis" small primary>Ver e aprovar</LinkButton> : <LinkButton to="/portal/entregaveis" small>Ver</LinkButton>,
      rank: 1,
    })),
    ...openInvoices.map(invoiceStep),
    ...waitingTickets.map<Step>((i) => ({
      key: i.id,
      tone: "warn",
      icon: <Headphones />,
      title: `A equipe respondeu: ${i.title}`,
      desc: "O chamado está aguardando a sua resposta.",
      action: <LinkButton to={`/portal/chamados/${i.id}`} small>Responder</LinkButton>,
      rank: 3,
    })),
  ].sort((a, b) => a.rank - b.rank);
  const openTotal = openInvoices.reduce((a, i) => a + Number(i.amount_cents ?? 0), 0);

  return (
    <>
      <Block title="Próximos passos" id="portal-proximos">
        {steps.length === 0 ? (
          <div className="vx-next__done">
            <CheckCircle2 aria-hidden="true" style={{ width: 20, height: 20, color: "var(--ok)", flexShrink: 0 }} />
            <span>Tudo em dia. Nada pendente com você agora.</span>
          </div>
        ) : (
          <ul className="vx-next">
            {steps.slice(0, 6).map((s) => (
              <NextStep key={s.key} tone={s.tone} icon={s.icon} title={s.title} desc={s.desc} action={s.action} />
            ))}
          </ul>
        )}
      </Block>

      <Block title="Resumo" id="portal-resumo">
        <KpiRow>
          <KpiTile label="Entregas para aprovar" value={formatInt(toApprove.length)} tone={toApprove.length > 0 ? "warn" : undefined} foot={<Link to="/portal/entregaveis">Entregáveis</Link>} />
          {me.can_see_finance ? (
            <KpiTile label="Em aberto para pagar" value={formatMoneyShort(openTotal)} foot={<Link to="/portal/financeiro">{openInvoices.length} fatura(s)</Link>} />
          ) : (
            <KpiTile label="Documentos" value={formatInt(me.counts.documents)} foot={<Link to="/portal/documentos">Biblioteca</Link>} />
          )}
          <KpiTile label="Chamados abertos" value={formatInt(openTickets.length)} foot={<Link to="/portal/chamados">Chamados</Link>} />
          <KpiTile label="Projetos em andamento" value={formatInt(activeProjects.length)} foot={<Link to="/portal/projetos">Projetos</Link>} />
        </KpiRow>
      </Block>

      {activeProjects.length > 0 ? (
        <Block title="Seus projetos" id="portal-projetos" actions={<Link to="/portal/projetos">Ver cronogramas</Link>}>
          <div className="vx-cards">
            {activeProjects.slice(0, 4).map((p) => {
              const current = p.phases.find((f) => f.status === "active") ?? p.phases.find((f) => f.status === "pending");
              return (
                <article key={p.id} className="vx-card vx-card--accent">
                  <div className="vx-card__head">
                    <div style={{ minWidth: 0 }}>
                      <h3 className="vx-card__title">{p.title}</h3>
                      <p className="vx-card__sub">{p.code}</p>
                    </div>
                    <FolderKanban aria-hidden="true" style={{ width: 18, height: 18, color: "var(--text-3)", flexShrink: 0 }} />
                  </div>
                  <Progress pct={p.progress_pct} />
                  <p className="text-2" style={{ margin: 0, fontSize: "var(--fs-13)" }}>
                    {current ? `Agora: ${current.name}` : "Todas as fases concluídas"}
                    {p.due_date ? ` · previsão ${formatDate(p.due_date)}` : ""}
                  </p>
                </article>
              );
            })}
          </div>
        </Block>
      ) : null}

      <div className="vx-grid vx-grid--2 vx-block">
        <RecentDocuments orgId={org.id} />
        <RecentActivity orgId={org.id} />
      </div>
    </>
  );
}

function RecentDocuments({ orgId }: { orgId: string }) {
  const q = useLibrary(orgId);
  const docs = (q.data?.items ?? []).filter((d) => d.kind !== "invoice").slice(0, 4);
  return (
    <Panel title="Documentos recentes" actions={<Link to="/portal/documentos">Ver todos</Link>}>
      {q.isLoading ? (
        <Skeleton lines={3} />
      ) : docs.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>Contratos, propostas, recibos e relatórios aparecem aqui.</p>
      ) : (
        <ul className="vx-stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {docs.map((d) => (
            <li key={d.id} className="vx-row vx-row--between" style={{ flexWrap: "nowrap", gap: "var(--sp-3)" }}>
              <span style={{ display: "flex", gap: "var(--sp-2)", minWidth: 0 }}>
                <FileText aria-hidden="true" style={{ width: 16, height: 16, flexShrink: 0, marginTop: 3, color: "var(--text-3)" }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 500, overflowWrap: "anywhere" }}>{d.title}</span>
                  <span className="text-2" style={{ fontSize: "var(--fs-12)" }}>
                    {d.type_label ?? KIND_LABEL[d.kind]} · {formatDate(d.occurred_at)}
                  </span>
                </span>
              </span>
              {d.pdf === "ready" ? <PdfButton itemId={d.id} small label="PDF" /> : d.pdf === "processing" ? <StatusPill tone="info">em preparação</StatusPill> : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function timelineText(e: TimelineEntry): Pick<TimelineItem, "title" | "tone"> {
  if (e.type === "approval") {
    return { title: `${e.actor ?? "Sua empresa"} ${e.status === "accepted" ? "aprovou" : "devolveu"} a entrega ${e.title}`, tone: e.status === "accepted" ? "ok" : "accent" };
  }
  if (e.type === "file") {
    return e.kind === "to_client"
      ? { title: `A equipe publicou o arquivo ${e.title}`, tone: "accent" }
      : { title: `${e.actor ?? "Sua empresa"} enviou o arquivo ${e.title}`, tone: "neutral" };
  }
  if (e.type === "terms") return { title: `${e.actor ?? "Você"} aceitou os ${e.title.toLowerCase()}`, tone: "neutral" };
  const label = KIND_LABEL[e.kind as ItemKind] ?? "Item";
  const st = e.status ? ITEM_STATUS[e.status]?.label : null;
  const t = ITEM_STATUS[e.status ?? ""]?.tone;
  return { title: `${label}: ${e.title}${st ? ` (${st})` : ""}`, tone: t === "ok" ? "ok" : t === "neutral" || !t ? "neutral" : "accent" };
}

function RecentActivity({ orgId }: { orgId: string }) {
  const q = useTimeline(orgId, 8);
  const items: TimelineItem[] = (q.data ?? []).map((e, i) => ({
    id: `${e.type}-${e.item_id ?? e.file_id ?? i}-${e.at}`,
    ...timelineText(e),
    meta: [formatDateTime(e.at), e.amount_cents ? formatMoney(e.amount_cents) : null].filter(Boolean).join(" · "),
  }));
  return (
    <Panel title="Atividade recente">
      {q.isLoading ? <Skeleton lines={4} /> : <Timeline items={items} empty="Nada por aqui ainda." />}
    </Panel>
  );
}

/** Portão do portal: acesso liberado e termos vigentes aceitos, antes de qualquer tela. */
export function TermsGate({ children }: { children: ReactNode }) {
  const org = useOrg();
  const me = usePortalMe(org.id);
  const accept = useAction((termId: string) => rpc("portal", "accept_terms", { p_term: termId }), {
    invalidate: [["portal"], ["core", "people", "me"]],
    success: "Termos aceitos. Bem-vindo!",
  });
  if (me.isLoading) return <Skeleton lines={4} />;
  if (me.error || !me.data) {
    return (
      <Panel>
        <EmptyState title="Seu acesso ao portal ainda não foi liberado">
          Peça à equipe da {org.name} para liberar o acesso com o seu e-mail. Se você já recebeu o convite, confira se entrou
          com o mesmo e-mail.
        </EmptyState>
      </Panel>
    );
  }
  const t = me.data.pending_term;
  if (t) {
    return (
      <div className="vx-stack vx-stack--lg" style={{ maxWidth: 820 }}>
        <div className="vx-hello" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="vx-hello__title">{t.title}</h1>
            <p className="vx-hello__sub">
              Antes de começar, leia e aceite os termos de uso da área do cliente. Versão {t.version}, publicada em{" "}
              {formatDate(t.published_at)}.
            </p>
          </div>
        </div>
        <Panel>
          <div className="vx-doc" style={{ maxHeight: "52vh", overflowY: "auto" }} tabIndex={0} aria-label="Texto dos termos">
            {t.body}
          </div>
        </Panel>
        <div className="vx-row vx-row--end">
          <Button variant="primary" icon={<CheckCircle2 />} loading={accept.isPending} onClick={() => accept.mutate(t.id)}>
            Li e aceito os termos
          </Button>
        </div>
        <p className="muted" style={{ fontSize: "var(--fs-12)" }}>
          Registramos o aceite com data, hora, a versão dos termos e um resumo técnico do acesso.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}
